import React, { useEffect, useState, useCallback } from "react";
import { adminClient } from "../../../lib/admin";
import { Search, Plus, RefreshCw, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  active: "bg-green-50 text-green-700", pending: "bg-yellow-50 text-yellow-700",
  suspended: "bg-orange-50 text-orange-700", banned: "bg-red-50 text-red-700",
  in_review: "bg-blue-50 text-blue-700", deleted: "bg-gray-100 text-gray-500",
};

export default function AdminUsers() {
  const client = adminClient();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [search, setSearch] = useState("");
  const [roleF, setRoleF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/users", { params: { search: search || undefined, role: roleF || undefined, status: statusF || undefined } });
      setUsers(data.users);
    } catch { /* handled by interceptor */ }
    finally { setLoading(false); }
  }, [search, roleF, statusF]);

  useEffect(() => { client.get("/admin/roles").then(({ data }) => { setRoles(data.roles); setStatuses(data.statuses); }).catch(() => {}); }, []);
  useEffect(() => { load(); }, [roleF, statusF]);

  const syncShopify = async () => {
    try { const { data } = await client.post("/admin/sync-shopify-customers"); toast.success(`Linked ${data.linked} customer(s), ${data.orders_cached} orders synced`); }
    catch { toast.error("Sync failed"); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="text-xl font-black font-display">Users &amp; Roles</h2>
        <div className="flex gap-2">
          <button onClick={syncShopify} data-testid="sync-shopify-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border border-black/15 px-3 py-2 hover:bg-taven-gray"><RefreshCw className="w-3.5 h-3.5" /> Sync Store</button>
          <button onClick={() => setCreating(true)} data-testid="create-user-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-taven-black text-white px-3 py-2 hover:bg-taven-orange"><Plus className="w-3.5 h-3.5" /> New User</button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex items-center border border-black/15 px-3 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email, phone…" data-testid="user-search" className="px-2 py-2 text-sm outline-none w-full" />
        </form>
        <select value={roleF} onChange={(e) => setRoleF(e.target.value)} data-testid="user-role-filter" className="border border-black/15 px-3 text-sm">
          <option value="">All roles</option>
          {roles.map((r) => <option key={r.name} value={r.name}>{r.label}</option>)}
        </select>
        <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="user-status-filter" className="border border-black/15 px-3 text-sm">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        {loading ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-taven-gold" /></div> :
          users.length === 0 ? <div className="p-8 text-center text-muted-foreground">No users found.</div> :
            <table className="w-full text-sm">
              <thead className="bg-taven-gray/60"><tr>{["Name", "Email", "Roles", "Status", "Store", "Joined"].map((h) => <th key={h} className="text-left px-3 py-2 font-bold uppercase text-[10px] tracking-wider">{h}</th>)}</tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} onClick={() => setSelected(u.id)} data-testid="user-row" className="border-t border-black/5 hover:bg-taven-gray/30 cursor-pointer">
                    <td className="px-3 py-2.5 font-semibold">{u.first_name} {u.last_name}</td>
                    <td className="px-3 py-2.5">{u.email}</td>
                    <td className="px-3 py-2.5"><div className="flex flex-wrap gap-1">{u.roles.map((r) => <span key={r} className="text-[10px] bg-taven-gray px-1.5 py-0.5 rounded">{r}</span>)}</div></td>
                    <td className="px-3 py-2.5"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${STATUS_COLORS[u.status] || ""}`}>{u.status}</span></td>
                    <td className="px-3 py-2.5">{u.shopify_linked ? "✓" : "—"}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>}
      </div>

      {selected && <UserDrawer userId={selected} roles={roles} statuses={statuses} onClose={() => setSelected(null)} onSaved={load} />}
      {creating && <CreateUser roles={roles} statuses={statuses} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

const Drawer = ({ children, onClose, title }) => (
  <div className="fixed inset-0 z-[70]" data-testid="user-drawer">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className="absolute right-0 top-0 h-full w-full max-w-lg bg-white overflow-y-auto p-6">
      <div className="flex items-center justify-between mb-5"><h3 className="font-black font-display text-lg">{title}</h3><button onClick={onClose} data-testid="drawer-close"><X className="w-5 h-5" /></button></div>
      {children}
    </div>
  </div>
);

const UserDrawer = ({ userId, roles, statuses, onClose, onSaved }) => {
  const client = adminClient();
  const [d, setD] = useState(null);
  const [selRoles, setSelRoles] = useState([]);
  const [status, setStatus] = useState("");
  const [newPw, setNewPw] = useState("");

  const load = useCallback(() => {
    client.get(`/admin/users/${userId}`).then(({ data }) => { setD(data); setSelRoles(data.user.roles); setStatus(data.user.status); }).catch(() => toast.error("Could not load user"));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <Drawer onClose={onClose} title="User"><Loader2 className="w-5 h-5 animate-spin text-taven-gold" /></Drawer>;
  const u = d.user;

  const toggleRole = (r) => setSelRoles(selRoles.includes(r) ? selRoles.filter((x) => x !== r) : [...selRoles, r]);
  const saveRoles = async () => { try { await client.post(`/admin/users/${userId}/roles`, { roles: selRoles }); toast.success("Roles updated"); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const saveStatus = async () => { try { await client.post(`/admin/users/${userId}/status`, { status }); toast.success("Status updated"); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const resetPw = async () => { if (newPw.length < 6) { toast.error("Min 6 chars"); return; } try { await client.post(`/admin/users/${userId}/reset-password`, { new_password: newPw }); setNewPw(""); toast.success("Password reset"); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const del = async () => { try { await client.delete(`/admin/users/${userId}`); toast.success("User archived"); onClose(); onSaved(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <Drawer onClose={onClose} title={`${u.first_name} ${u.last_name}`}>
      <p className="text-sm text-muted-foreground">{u.email} · {u.phone || "no phone"}</p>
      <p className="text-xs text-muted-foreground mt-1">Store: {u.shopify_linked ? `Linked (${u.shopify_customer_id})` : "Not linked"} · Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</p>

      <div className="mt-5 border-t border-black/10 pt-4">
        <p className="text-xs font-bold uppercase tracking-wider mb-2">Roles</p>
        <div className="grid grid-cols-2 gap-1.5">
          {roles.map((r) => (
            <label key={r.name} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={selRoles.includes(r.name)} onChange={() => toggleRole(r.name)} data-testid={`role-${r.name}`} /> {r.label}</label>
          ))}
        </div>
        <button onClick={saveRoles} data-testid="save-roles" className="mt-3 text-xs font-bold uppercase tracking-wider bg-taven-black text-white px-4 py-2 hover:bg-taven-orange">Save Roles</button>
      </div>

      <div className="mt-5 border-t border-black/10 pt-4 flex items-end gap-2">
        <div><p className="text-xs font-bold uppercase tracking-wider mb-2">Status</p>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="user-status-select" className="border border-black/15 px-3 py-2 text-sm">{statuses.map((s) => <option key={s} value={s}>{s}</option>)}</select>
        </div>
        <button onClick={saveStatus} data-testid="save-status" className="text-xs font-bold uppercase tracking-wider border border-black/15 px-4 py-2 hover:bg-taven-gray">Update</button>
      </div>

      <div className="mt-5 border-t border-black/10 pt-4">
        <p className="text-xs font-bold uppercase tracking-wider mb-2">Reset Password</p>
        <div className="flex gap-2"><input value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password" data-testid="reset-pw-input" className="border border-black/15 px-3 py-2 text-sm flex-1" /><button onClick={resetPw} data-testid="reset-pw-btn" className="text-xs font-bold uppercase bg-taven-black text-white px-4 py-2 hover:bg-taven-orange">Reset</button></div>
      </div>

      <div className="mt-5 border-t border-black/10 pt-4 grid grid-cols-4 gap-2 text-center">
        {[["Orders", d.orders.length], ["Certs", d.certificates.length], ["Reviews", d.reviews.length], ["Tickets", d.tickets.length]].map(([l, v]) => (
          <div key={l} className="bg-taven-gray/50 p-3"><p className="text-lg font-black">{v}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p></div>
        ))}
      </div>

      <button onClick={del} data-testid="delete-user" className="mt-6 text-xs font-bold uppercase tracking-wider text-taven-red hover:underline">Archive / Delete User</button>
    </Drawer>
  );
};

const CreateUser = ({ roles, statuses, onClose, onSaved }) => {
  const client = adminClient();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "", roles: ["customer"], status: "active" });
  const save = async (e) => {
    e.preventDefault();
    try { await client.post("/admin/users", f); toast.success("User created"); onSaved(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const toggleRole = (r) => setF({ ...f, roles: f.roles.includes(r) ? f.roles.filter((x) => x !== r) : [...f.roles, r] });
  return (
    <Drawer onClose={onClose} title="Create User">
      <form onSubmit={save} className="space-y-3" data-testid="create-user-form">
        <div className="grid grid-cols-2 gap-2">
          <input required value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} placeholder="First name" data-testid="cu-first" className="border border-black/15 px-3 py-2.5 text-sm" />
          <input value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} placeholder="Last name" data-testid="cu-last" className="border border-black/15 px-3 py-2.5 text-sm" />
        </div>
        <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" data-testid="cu-email" className="w-full border border-black/15 px-3 py-2.5 text-sm" />
        <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Phone" data-testid="cu-phone" className="w-full border border-black/15 px-3 py-2.5 text-sm" />
        <input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" data-testid="cu-password" className="w-full border border-black/15 px-3 py-2.5 text-sm" />
        <div>
          <p className="text-xs font-bold uppercase tracking-wider mb-2">Roles</p>
          <div className="grid grid-cols-2 gap-1.5">{roles.map((r) => <label key={r.name} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.roles.includes(r.name)} onChange={() => toggleRole(r.name)} /> {r.label}</label>)}</div>
        </div>
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="border border-black/15 px-3 py-2.5 text-sm">{statuses.filter((s) => s !== "deleted").map((s) => <option key={s} value={s}>{s}</option>)}</select>
        <button data-testid="cu-submit" className="w-full bg-taven-black text-white font-bold uppercase tracking-wider py-3 hover:bg-taven-orange">Create User</button>
      </form>
    </Drawer>
  );
};
